import { CommonModule } from '@angular/common';
import {
  NgbModal,
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';

import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import {
  Component,
  OnInit,
  inject,
  Injector,
  ViewChild,
  computed,
  OnDestroy,
  Signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { BreadcrumbService } from 'xng-breadcrumb';
import { AnnexesListComponent, GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';

import {
  CreateUpdateSupplierPurchaseOrderDto,
  DayBookService,
  SupplierPurchaseOrderDefaultValueDto,
  SupplierPurchaseOrderDto,
  SupplierPurchaseOrderService,
} from '@digiwall/goliath-documents/proxy';
import { AddressDto, SupplierService } from '@digiwall/goliath-contacts/proxy';
import { ClassificationTypeService, DocRole } from '@digiwall/goliath-core/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { ReportViewerComponent } from '@odyssey/reporting';
import {
  ChatComponent,
  DeactivatableComponentService,
  DwDatePickerComponent,
  DwInputAutoCompleteDirective,
  DwInputRequiredDirective,
  OdysseyOffcanvas,
} from '@digiwall/core';
import { SupplierPurchaseOrderLinesEditorComponent } from './supplier-purchase-order-lines/supplier-purchase-order-lines-editor.component';
import { BaseSupplierDocDetail } from '../../base-supplier-docs/base-supplier-doc-detail';
import {
  CounterComponent,
  DropdownMenu,
  DropdownMenuComponent,
  PanelComponent,
} from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import { DwNavLinkDirective } from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocSupplierAddressesComponent } from '../../base-docs/detail/doc-supplier-addresses/doc-supplier-addresses.component';
import { DocSupplierOthersComponent } from '../../base-docs/detail/doc-supplier-others/doc-supplier-others.component';
import { AsyncPipe } from '@angular/common';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  DocNumberTagComponent,
  DocumentCoreCardComponent,
  DocumentSupplierCardComponent,
  SupplierDocumentDateCardComponent,
  SupplierDocumentStatusCardComponent,
} from '../../../components';
import { NgSelectModule } from '@ng-select/ng-select';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import { SupplierDeliveryMode } from '../../base-supplier-docs/supplier-delivery-mode.enum';

@Component({
  selector: 'document-supplier-quote-detail',
  templateUrl: './supplier-purchase-order-detail.component.html',
  styleUrl: './supplier-purchase-order-detail.component.scss',
  providers: [DocTemplateService, SubscriptionService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    SupplierPurchaseOrderLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocSupplierAddressesComponent,
    DocSupplierOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    DialogBoxComponent,
    ReportViewerComponent,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentSupplierCardComponent,
    SupplierDocumentDateCardComponent,
    SupplierDocumentStatusCardComponent,
    DwDatePickerComponent,
    DwInputRequiredDirective,
    DwInputAutoCompleteDirective,
    NgSelectModule,
    DropdownMenuComponent,
    DocNumberTagComponent,
  ],
})
export class SupplierPurchaseOrderDetailComponent
  extends BaseSupplierDocDetail<
    SupplierPurchaseOrderService,
    SupplierPurchaseOrderDto,
    CreateUpdateSupplierPurchaseOrderDto,
    SupplierPurchaseOrderDefaultValueDto
  >
  implements OnInit, OnDestroy
{
  @ViewChild('supplierPurchaseOrderLineGrid') lineGrid: SupplierPurchaseOrderLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.SupplierPurchaseOrder;

  protected modalService = inject(NgbModal);

  actions: Signal<DropdownMenu> = computed(() => ({
    groups: [
      this.baseActionsThirdPartyDoc().concat(
        {
          label: '::GeneratePdf',
          icon: 'digi-out-pdf-file',
          action: () => this.generatePdf(this.reportViewer),
        },
        {
          label: '::Copy',
          icon: 'digi-out-copy',
          action: () => this.copy(),
        },
      ),
      [
        {
          label: 'Doc::GenerateDocPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () => this.openDesigner(),
          requiredPolicy: DocReportPermission.Create,
        },
      ],
    ],
  }));

  protected saveActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('::Save'),
        action: () => this.save(),
        // If not validated (draft), then you can always save
        // If validated (non-draft), then you can no longer save (validated auto)
        disabled: () =>
          !(
            // If draft -> can save
            (
              !this.isValidated() ||
              // Else NOT Draft
              // Must be dirty and linecount must be 0
              (this.form.dirty && this.lineCount() == 0)
            )
          ),
      },
    ];

    if (!this.isValidated()) {
      btns.push({
        label: this.localizationService.instant('Doc::SaveAsDraft'),
        action: () => this.saveAsDraft(),
        disabled: () => !this.form.dirty,
      });
    }
    return btns;
  });

  deliveryAddressSelected$ = new BehaviorSubject<AddressDto>(null);

  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  salesPersonStore = inject(SalesPersonStore);

  totalAmount: number = 0;
  totalAmountVatIncluded: number = 0;

  deliveryMode = SupplierDeliveryMode;

  showSearch: boolean;

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    service: SupplierPurchaseOrderService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    quoteTemplateService: DocTemplateService,
    injector: Injector,
    thirdPartyService: SupplierService,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      service,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      quoteTemplateService,
      injector,
      thirdPartyService,
      config,
      confirmationService,
      deactivableComponentService,
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }

  override initFormObservers() {
    super.initFormObservers();
    this.form
      .get('thirdPartyId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(thirdPartyId => {
        if (thirdPartyId != null) {
          this.supplierService.get(thirdPartyId).subscribeOnce(supplier => {
            this.form.get('supplierDeliveryModeId').setValue(supplier.supplierDeliveryMode);
          });
        }
      });
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      estimatedAvailabilityDate: [null],
      additionalCostPricePercentage: [null],
      supplierOrderStatusId: [null, Validators.required],
    });
  }

  getEntityCallback(): void {
    super.getEntityCallback();

    this.loadAddresses(this.entity.thirdPartyId);

    this.lineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }

  protected override async afterGetEntity(response: SupplierPurchaseOrderDto): Promise<void> {
    await super.afterGetEntity(response);
    this.subscribeToFormValueChanges();
  }

  protected override afterSave(): void {
    super.afterSave();
    this.subscribeToFormValueChanges();
  }

  public override canDeactivate = async () => {
    if (
      this.isValidated() &&
      this.lineCount() > 0 &&
      (this.form.dirty || !this.isStockValidated())
    ) {
      this.canDeactivateTitle = 'Doc::CustomerQuote:NotValidatedWishToContinue';
    } else {
      this.canDeactivateTitle = null;
    }
    return !this.form.dirty;
  };
}
